import React from "react";
import { Archive, CircleCheck, Clock3, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Recording } from "../../api/types";
import FilterMultiSelect from "../FilterMultiSelect";
import { FiltersPopover, RangeFilter } from "../FilterControls";
import { recordingDurationSeconds } from "../../utils";
import {
  defaultSessionFilters,
  sessionFilterCount,
  type SessionFilterValues,
} from "./sessionFilterModel";

export default function SessionFilterControls({
  title,
  recordings,
  filters,
  onChange,
}: {
  title: React.ReactNode;
  recordings: Recording[];
  filters: SessionFilterValues;
  onChange: (patch: Partial<SessionFilterValues>) => void;
}) {
  const { t } = useTranslation();
  const count = sessionFilterCount({ ...filters, query: "" });
  const update = onChange;
  // Bounds must not move when search, archive, or status filters change.
  const durations = recordings.map(recordingDurationSeconds);
  const rangeMin = durations.length ? Math.floor(Math.min(...durations)) : 0;
  const rangeMax = durations.length ? Math.ceil(Math.max(...durations)) : 0;
  const statusOptions = [
    { id: "completed", label: t("activity.analyzed") },
    { id: "analyzing", label: t("analyzing_ellipsis") },
    { id: "not_analyzed", label: t("sessions_not_analyzed") },
    { id: "failed", label: t("failed") },
    { id: "cancelled", label: t("cancelled") },
    { id: "not_synced", label: t("activity.not_synced") },
    { id: "recording_too_short", label: t("recording_too_short") },
  ];
  const archiveOptions = [
    { id: "active", label: t("recording_active") },
    { id: "archived", label: t("recording_archived") },
  ];
  const archiveSelection = filters.archive === "all" ? ["active", "archived"] : [filters.archive];
  const statusActive = filters.status.length !== statusOptions.length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex max-w-full flex-wrap items-center gap-3">
          {title}
          <label className="relative w-60 max-w-full">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-dim"
            />
            <input
              type="search"
              aria-label={t("activity.session_search")}
              placeholder={t("activity.session_search")}
              value={filters.query}
              onChange={event => update({ query: event.target.value })}
              className="h-[38px] w-full rounded-lg border border-outline bg-surface pl-9 pr-3 text-sm text-text-mid focus-visible:outline-info"
            />
          </label>
        </div>
        <FiltersPopover
          count={count}
          onClear={() => update({ ...defaultSessionFilters, query: filters.query })}
        >
          <div
            className={`max-w-full [&>div]:min-w-0 ${statusActive ? "[&>div>button]:border-info/30 [&>div>button]:bg-info/5 [&>div>button]:text-info" : ""}`}
          >
            <FilterMultiSelect
              ariaLabel={t("activity.session_status")}
              icon={<CircleCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
              clearAction={
                statusActive
                  ? {
                      label: t("filterControls.remove", { filter: t("activity.session_status") }),
                      onClear: () => update({ status: defaultSessionFilters.status }),
                    }
                  : undefined
              }
              value={filters.status}
              onChange={status => update({ status })}
              allLabel={`${t("activity.session_status")}: ${t("filterControls.any")}`}
              emptyLabel={t("activity.no_statuses")}
              selectAllLabel={t("activity.all_statuses")}
              multipleLabel={count => t("activity.status_count", { count })}
              options={statusOptions}
            />
          </div>
          <div className="max-w-full [&>div]:min-w-0">
            <FilterMultiSelect
              ariaLabel={t("filterControls.visibility")}
              icon={<Archive aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
              clearAction={
                filters.archive !== "active"
                  ? {
                      label: t("filterControls.remove", { filter: t("filterControls.visibility") }),
                      onClear: () => update({ archive: "active" }),
                    }
                  : undefined
              }
              value={archiveSelection}
              onChange={selection =>
                update({ archive: selection.length === 2 ? "all" : selection[0] })
              }
              triggerLabelPrefix={`${t("filterControls.visibility")}: `}
              keepOne
              allLabel={t("recording_filter_all")}
              options={archiveOptions}
              description={t("filterControls.archive_hint")}
            />
          </div>
          <RangeFilter
            label={t("duration")}
            icon={<Clock3 className="h-4 w-4" />}
            rangeMin={rangeMin}
            rangeMax={rangeMax}
            value={[filters.min, filters.max]}
            onChange={([min, max]) => update({ min, max })}
            duration
          />
        </FiltersPopover>
      </div>
    </div>
  );
}
